import React, { useEffect, useState } from 'react';
import queryString from 'query-string';
import { Link, useHistory } from 'react-router-dom';
import {
  BoxV2 as Box,
  Flex,
  Table,
  FilterType,
  TableFilter,
  useTable,
  useFilters,
} from 'portal-commons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faShareNodes,
  faCalendarDay,
} from '@fortawesome/pro-regular-svg-icons';

import {
  SearchArea,
  CampaignReviewSection,
  CampaignSuspendSection,
  CampaignChartsSection,
} from '../components';
import {
  getBrandNameSuggestions,
  getCampaignList,
  queryEventList,
} from '../apis';
import { EventData } from '../../Events/types';
import { getAllEventTypes } from '../../Events/apiServices';
import { MAX_EVENT_RECORDS } from '../../Events/constants';
import EventsListingRow from '../../Events/components/EventsListingRow';
import { Loader } from '../../../../shared_elements';
import { toastFlashMessage } from '../../../../utils';
import { SearchResult } from '../types';
import { dateRangePresets, Today } from '../utils';
import '../../Events/assets/events-listing-module.scss';
import { RCS_CAMPAIGN_ENABLED } from '@/constants';
import { getAuthenticatedUserSuggestions } from '@/apis/services';
import { constantizeAuthenticatedUser } from '@/utils/string';

export const FILTER_CONFIG = {
  eventType: {
    type: FilterType.Dropdown,
    options: [],
    label: 'Event Type',
  },
  campaignUid: {
    type: FilterType.Text,
    label: 'Campaign ID',
    placeholder: 'Enter Campaign ID',
    width: 180,
  },
  brandUid: {
    type: FilterType.Text,
    label: 'Brand ID',
    placeholder: 'Enter Brand ID',
    width: 180,
  },
  brandName: {
    type: FilterType.Text,
    label: 'Brand DBA Name',
    placeholder: 'Enter Brand DBA Name',
    suggestions: [],
    width: 180,
  },
  dateRange: {
    type: FilterType.DateRange,
    label: 'Date Range',
    placeholder: 'Select Date Range',
    delayUpdate: false,
    dateRangePresets: [
      {
        label: 'Today',
        start: Today,
        end: Today,
      },
      ...dateRangePresets.slice(1),
    ],
  },
  authenticatedUser: {
    type: FilterType.Text,
    label: 'User',
    placeholder: 'Enter User',
    suggestions: [],
    width: 250,
  },
};

export const headRows = [
  { label: 'EVENT TYPE', id: 'eventType', sortable: true },
  { label: 'CATEGORY', id: 'categoryType', sortable: false },
  { label: 'CAMPAIGN ID', id: 'campaignUid', sortable: true },
  { label: 'BRAND ID', id: 'brandUid', sortable: true },
  { label: 'DATE & TIME', id: 'createDate', sortable: true },
  { label: 'USER', id: 'authenticatedUser', sortable: false },
  { label: 'DESCRIPTION', id: 'description', sortable: false },
];

const FILTER_LOADERS = {
  suggestion: {
    brandName: {
      minLength: 2,
      load: (value: string) => getBrandNameSuggestions({ prefix: value }),
    },
    authenticatedUser: {
      minLength: 2,
      load: (value: string) => getAuthenticatedUserSuggestions(value),
    },
  },
};

const Dashboard: React.FC = () => {
  const history = useHistory();
  const filter = useFilters({
    configs: FILTER_CONFIG,
    loaders: FILTER_LOADERS,
  });
  const [loader, setLoader] = useState(true);
  const [registeredCount, setRegisteredCount] = useState(0);
  const [reviewCount, setReviewCount] = useState(0);
  const [suspendedCount, setSuspendedCount] = useState(0);

  const eventTable = useTable<EventData>(
    async (tableFilter) => {
      const authenticatedUser = constantizeAuthenticatedUser(
        filter.appliedFilters.authenticatedUser
      );
      const query = {
        ...tableFilter,
        ...filter.additionalFilters,
        ...filter.appliedFilters,
        ...(authenticatedUser ? { authenticatedUser } : {}),
      };
      const response = await queryEventList(query);
      return {
        data: response?.records || [],
        page: response?.page || 1,
        total: response?.totalRecords || 0,
        rowsPerPage: response?.recordsPerPage || 10,
      };
    },
    {
      cappingTotal: MAX_EVENT_RECORDS,
      deps: [filter.additionalFilters, filter.appliedFilters],
      defaultFilter: {
        sortField: 'createDate',
        ascendingOrder: false,
      },
    }
  );

  const fetchFilterOptions = async () => {
    const { setOptions } = filter;
    const eventTypes = await getAllEventTypes();
    if (eventTypes.length === 0) return;
    setOptions(
      'eventType',
      eventTypes.map((item: { id: string; displayName: string }) => ({
        label: item.id,
        value: item.id,
      }))
    );
  };

  const fetchCampaignCounts = async (mnoStatus = 'REGISTERED') => {
    const campaigns = await getCampaignList({
      mnoStatus,
      ...(RCS_CAMPAIGN_ENABLED || mnoStatus === 'SUSPENDED'
        ? { channelId: '10DLC' }
        : {}),
    });
    if (campaigns) {
      switch (mnoStatus) {
        case 'REVIEW':
          setReviewCount(campaigns.totalRecords);
          break;
        case 'SUSPENDED':
          setSuspendedCount(campaigns.totalRecords);
          break;
        case 'REGISTERED':
        default:
          setRegisteredCount(campaigns.totalRecords);
          break;
      }
    }
  };

  const handleSearchNotFound = (result: SearchResult) => {
    history.push(`/dashboard/not-found?${queryString.stringify(result)}`);
  };

  useEffect(() => {
    if (!loader) {
      const loginFlag = localStorage.getItem('loginSuccess');
      if (loginFlag === '1') {
        toastFlashMessage('Logged in successfully', 'success');
        localStorage.removeItem('loginSuccess');
      }
    }
  }, [loader]);

  useEffect(() => {
    const fetchAllData = async () => {
      try {
        await Promise.all([
          fetchCampaignCounts('REGISTERED'),
          fetchCampaignCounts('REVIEW'),
          fetchCampaignCounts('SUSPENDED'),
          fetchFilterOptions(),
        ]);
      } finally {
        setLoader(false);
      }
    };
    fetchAllData();
  }, []);

  return (
    <Box sx={{ px: 'xxl', py: 'l', width: '100%' }}>
      {loader ? (
        <Loader />
      ) : (
        <>
          <HeaderSection count={registeredCount} />
          <SearchArea onNoSearchResult={handleSearchNotFound} />
          <Flex
            sx={{
              alignItems: 'flex-start',
              width: '100%',
              mt: 20,
              gap: 14,
              justifyContent: 'space-between',
              flexWrap: 'wrap',
            }}
          >
            <CampaignReviewSection count={reviewCount} />
            <CampaignSuspendSection count={suspendedCount} />
            <CampaignChartsSection />
          </Flex>
          <Flex
            sx={{
              justifyContent: 'center',
              alignItems: 'center',
              width: '100%',
              mt: 'xl',
              mb: 's',
            }}
          >
            <FontAwesomeIcon
              icon={faCalendarDay}
              style={{ fontSize: 26, color: '#CACC4F', marginRight: 8 }}
            />
            <Box
              sx={{
                fontSize: 'H600',
                fontWeight: 600,
                lineHeight: 'normal',
                color: 't.black80',
              }}
            >
              Events
            </Box>
          </Flex>
          <Box
            className="events-filter-wrapper"
            sx={{ mb: 's', borderColor: '#B3B7B8' }}
          >
            <TableFilter
              portal="mno"
              configs={filter.configs}
              candidateValues={filter.candidateFilters}
              appliedValues={filter.appliedFilters}
              onCandidateValuesChange={filter.handleEdit}
              onAppliedValuesChange={filter.handleApply}
              className="filters"
              data-testid="events"
            />
          </Box>
          <Box sx={{ width: '100%' }} className="events-table-wrapper">
            <Table
              testId="eventsTable"
              headRows={headRows}
              emptyState="no events to view"
              loading={eventTable.loading}
              tableData={eventTable.tableData.map((record, index) => {
                return <EventsListingRow key={index} event={record} />;
              })}
              handleChangePage={eventTable.handleChangePage}
              createSortHandler={eventTable.handleSorting}
              filter={eventTable.filter}
              pagination={eventTable.pagination}
            />
          </Box>
        </>
      )}
    </Box>
  );
};

const HeaderSection: React.FC<{ count: number }> = ({ count }) => {
  return (
    <Flex
      sx={{
        alignItems: 'center',
        mb: 24,
        position: 'relative',
      }}
    >
      <FontAwesomeIcon
        icon={faShareNodes}
        style={{ color: '#B6B847', marginRight: 12, fontSize: 64 }}
      />
      <Box
        as="h1"
        sx={{
          color: '#000',
          fontSize: 36,
          fontWeight: 800,
          lineHeight: 'normal',
        }}
      >
        Network Overview
      </Box>
      <Link
        to="/campaigns?ascendingOrder=false&mnoStatus=REGISTERED&sortField=createDate&status=ACTIVE"
        style={{
          position: 'absolute',
          left: 70,
          bottom: -6,
          fontSize: 16,
          fontWeight: 400,
          textDecoration: 'none',
          color: '#4D5659',
        }}
        data-testid="dashboardRegisteredCampaignLink"
      >
        <span style={{ fontWeight: 800 }} data-testid="registeredCampaignCount">
          {count}{' '}
        </span>
        {RCS_CAMPAIGN_ENABLED
          ? 'Registered 10DLC Campaigns'
          : 'Registered Campaigns'}
      </Link>
    </Flex>
  );
};

export default Dashboard;
